import React, {
  useEffect,
  useMemo,
} from "react";

import {
  FiRefreshCcw,
  FiSearch,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";

/* =========================================================
   CONSTANTS
========================================================= */

const PRODUCT_CONDITIONS = [
  {
    value: "new",
    label: "New",
  },

  {
    value: "used",
    label: "Used",
  },

  {
    value: "refurbished",
    label: "Refurbished",
  },

  {
    value: "open-box",
    label: "Open Box",
  },
];


const PRODUCT_TYPES = [
  {
    value: "physical",
    label: "Physical",
  },

  {
    value: "digital",
    label: "Digital",
  },

  {
    value: "service",
    label: "Service",
  },
];


const PRODUCT_STATUSES = [
  {
    value: "active",
    label: "Active",
  },

  {
    value: "inactive",
    label: "Inactive",
  },

  {
    value: "draft",
    label: "Draft",
  },
];


const STOCK_STATUSES = [
  {
    value: "in-stock",
    label: "In Stock",
  },

  {
    value: "low-stock",
    label: "Low Stock",
  },

  {
    value: "out-of-stock",
    label: "Out of Stock",
  },
];


const SORT_OPTIONS = [
  {
    value: "createdAt",
    label: "Created Date",
  },

  {
    value: "updatedAt",
    label: "Updated Date",
  },

  {
    value: "name",
    label: "Product Name",
  },

  {
    value: "sellingPrice",
    label: "Selling Price",
  },

  {
    value: "purchasePrice",
    label: "Purchase Price",
  },

  {
    value: "stockQty",
    label: "Stock Quantity",
  },

  {
    value: "rating",
    label: "Rating",
  },
];


/* =========================================================
   HELPERS
========================================================= */

const normalizeId = (
  value
) => {
  if (!value) {
    return "";
  }

  if (
    typeof value ===
    "string"
  ) {
    return value;
  }

  if (
    typeof value ===
    "object"
  ) {
    return (
      value._id ||
      value.id ||
      ""
    );
  }

  return String(value);
};


/* =========================================================
   GET PARENT ID
========================================================= */

const getParentId = (
  category
) => {
  if (
    !category?.parentCategory
  ) {
    return "";
  }

  return normalizeId(
    category.parentCategory
  );
};


/* =========================================================
   EXTRACT CATEGORY RESPONSE
========================================================= */

const extractCategories = (
  response
) => {
  if (
    Array.isArray(
      response?.data
    )
  ) {
    return response.data;
  }

  if (
    Array.isArray(
      response?.data
        ?.categories
    )
  ) {
    return response.data
      .categories;
  }

  if (
    Array.isArray(
      response?.categories
    )
  ) {
    return response.categories;
  }

  return [];
};


/* =========================================================
   CATEGORY MAP
========================================================= */

const buildCategoryMap = (
  categories
) => {
  const map =
    new Map();

  categories.forEach(
    (
      category
    ) => {
      const id =
        normalizeId(
          category?._id
        );

      if (id) {
        map.set(
          id,
          category
        );
      }
    }
  );

  return map;
};


/* =========================================================
   BUILD CATEGORY PATH
========================================================= */

const buildCategoryPath = (
  category,
  categoryMap
) => {
  if (!category) {
    return "";
  }

  if (
    category?.path
  ) {
    return category.path;
  }

  if (
    category?.label
  ) {
    return category.label;
  }

  const parts =
    [];

  const visited =
    new Set();

  let current =
    category;

  while (
    current
  ) {
    const id =
      normalizeId(
        current?._id
      );

    if (
      id &&
      visited.has(id)
    ) {
      break;
    }

    if (id) {
      visited.add(id);
    }

    if (
      current?.name
    ) {
      parts.unshift(
        current.name
      );
    }

    const parentId =
      getParentId(
        current
      );

    if (
      !parentId
    ) {
      break;
    }

    current =
      categoryMap.get(
        parentId
      );
  }

  return parts.join(
    " > "
  );
};


/* =========================================================
   CHECK DESCENDANT
========================================================= */

const isDescendantOf = ({
  candidate,

  ancestorId,

  categoryMap,
}) => {
  if (
    !candidate ||
    !ancestorId
  ) {
    return false;
  }

  const candidateId =
    normalizeId(
      candidate?._id
    );

  if (
    candidateId ===
    ancestorId
  ) {
    return false;
  }

  const visited =
    new Set();

  let parentId =
    getParentId(
      candidate
    );

  while (
    parentId
  ) {
    if (
      parentId ===
      ancestorId
    ) {
      return true;
    }

    if (
      visited.has(
        parentId
      )
    ) {
      break;
    }

    visited.add(
      parentId
    );

    const parent =
      categoryMap.get(
        parentId
      );

    if (!parent) {
      break;
    }

    parentId =
      getParentId(
        parent
      );
  }

  return false;
};


/* =========================================================
   COMPONENT
========================================================= */

const ProductFilters = ({
  filters,

  setFilters,

  loading,

  onSearch,

  onReset,
}) => {
  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  /* =======================================================
     CATEGORY STATE
  ======================================================= */

  const [
    categories,
    setCategories,
  ] =
    React.useState([]);


  const [
    categoryLoading,
    setCategoryLoading,
  ] =
    React.useState(false);


  const [
    categoryError,
    setCategoryError,
  ] =
    React.useState("");


  /* =======================================================
     LOAD ALL ACTIVE CATEGORIES

     Dedicated dropdown API returns
     every hierarchy level.
  ======================================================= */

  useEffect(
    () => {
      let active =
        true;


      const fetchCategories =
        async () => {
          try {
            setCategoryLoading(
              true
            );

            setCategoryError(
              ""
            );


            const response =
              await api.post(
                config
                  .getInventoryCategoryDropdown,

                {
                  search:
                    "",

                  includeInactive:
                    false,

                  excludeCategoryId:
                    "",
                }
              );


            if (!active) {
              return;
            }


            if (
              response?.status !==
                "success" &&
              response?.success !==
                true
            ) {
              throw new Error(
                response?.message ||
                  "Unable to load categories."
              );
            }


            setCategories(
              extractCategories(
                response
              )
            );

          } catch (
            error
          ) {
            console.error(
              "Product category dropdown error:",
              error
            );


            if (
              active
            ) {
              setCategories(
                []
              );

              setCategoryError(
                error?.message ||
                  "Unable to load categories."
              );
            }

          } finally {

            if (
              active
            ) {
              setCategoryLoading(
                false
              );
            }
          }
        };


      fetchCategories();


      return () => {
        active =
          false;
      };

    },
    [
      api,
    ]
  );


  /* =======================================================
     CATEGORY MAP
  ======================================================= */

  const categoryMap =
    useMemo(
      () =>
        buildCategoryMap(
          categories
        ),
      [
        categories,
      ]
    );


  /* =======================================================
     ROOT CATEGORIES
  ======================================================= */

  const rootCategories =
    useMemo(
      () => {
        return categories
          .filter(
            (
              category
            ) =>
              !getParentId(
                category
              )
          )
          .sort(
            (
              a,
              b
            ) => {
              const sortA =
                Number(
                  a?.sortOrder ||
                    0
                );

              const sortB =
                Number(
                  b?.sortOrder ||
                    0
                );


              if (
                sortA !==
                sortB
              ) {
                return (
                  sortA -
                  sortB
                );
              }


              return String(
                a?.name ||
                  ""
              ).localeCompare(
                String(
                  b?.name ||
                    ""
                )
              );
            }
          );
      },
      [
        categories,
      ]
    );


  /* =======================================================
     SELECTED ROOT
  ======================================================= */

  const selectedRootId =
    normalizeId(
      filters?.category
    );


  const selectedRoot =
    useMemo(
      () =>
        categoryMap.get(
          selectedRootId
        ) ||
        null,
      [
        categoryMap,
        selectedRootId,
      ]
    );


  /* =======================================================
     ALL DESCENDANTS

     IMPORTANT:

     This replaces old direct-child-only logic.

     Example:

     Laptop
       Business Laptop
         Dell
           Latitude

     All three descendants become available.
  ======================================================= */

  const descendantCategories =
    useMemo(
      () => {
        if (
          !selectedRootId
        ) {
          return [];
        }


        return categories
          .filter(
            (
              category
            ) =>
              isDescendantOf({
                candidate:
                  category,

                ancestorId:
                  selectedRootId,

                categoryMap,
              })
          )
          .map(
            (
              category
            ) => ({
              ...category,

              _path:
                buildCategoryPath(
                  category,
                  categoryMap
                ),
            })
          )
          .sort(
            (
              a,
              b
            ) =>
              String(
                a?._path ||
                  ""
              ).localeCompare(
                String(
                  b?._path ||
                  ""
                )
              )
          );
      },
      [
        categories,
        categoryMap,
        selectedRootId,
      ]
    );


  /* =======================================================
     CLEAR INVALID CHILD WHEN ROOT CHANGES
  ======================================================= */

  useEffect(
    () => {
      const selectedChildId =
        normalizeId(
          filters?.subCategory
        );


      if (
        !selectedChildId
      ) {
        return;
      }


      if (
        !selectedRootId
      ) {
        setFilters(
          (
            previous
          ) => ({
            ...previous,

            subCategory:
              "",
          })
        );

        return;
      }


      const stillValid =
        descendantCategories.some(
          (
            category
          ) =>
            normalizeId(
              category?._id
            ) ===
            selectedChildId
        );


      if (
        !stillValid
      ) {
        setFilters(
          (
            previous
          ) => ({
            ...previous,

            subCategory:
              "",
          })
        );
      }

    },
    [
      descendantCategories,
      filters?.subCategory,
      selectedRootId,
      setFilters,
    ]
  );


  /* =======================================================
     COMMON CHANGE
  ======================================================= */

  const handleChange =
    (
      event
    ) => {
      const {
        name,
        value,
        type,
        checked,
      } =
        event.target;


      setFilters(
        (
          previous
        ) => ({
          ...previous,

          [name]:
            type ===
            "checkbox"
              ? checked
              : value,
        })
      );
    };


  /* =======================================================
     MAIN CATEGORY CHANGE

     Reset final child category.
  ======================================================= */

  const handleCategoryChange =
    (
      event
    ) => {
      const value =
        event.target
          .value;


      setFilters(
        (
          previous
        ) => ({
          ...previous,

          category:
            value,

          subCategory:
            "",
        })
      );
    };


  /* =======================================================
     PRICE INPUT
  ======================================================= */

  const handleNumberChange =
    (
      event
    ) => {
      const {
        name,
        value,
      } =
        event.target;


      /*
       * Allows:
       * 100
       * 100.50
       *
       * Prevents:
       * letters
       * multiple decimals
       */

      let sanitizedValue =
        String(value)
          .replace(
            /[^0-9.]/g,
            ""
          );


      const firstDot =
        sanitizedValue.indexOf(
          "."
        );


      if (
        firstDot !== -1
      ) {
        sanitizedValue =
          sanitizedValue.slice(
            0,
            firstDot + 1
          ) +
          sanitizedValue
            .slice(
              firstDot + 1
            )
            .replace(
              /\./g,
              ""
            );
      }


      setFilters(
        (
          previous
        ) => ({
          ...previous,

          [name]:
            sanitizedValue,
        })
      );
    };


  /* =======================================================
     ENTER SEARCH
  ======================================================= */

  const handleKeyDown =
    (
      event
    ) => {
      if (
        event.key !==
        "Enter"
      ) {
        return;
      }


      event.preventDefault();


      if (
        !loading
      ) {
        onSearch?.();
      }
    };


  /* =======================================================
     CHILD OPTION LABEL

     Main root:
     Laptop

     Options:

     Business Laptop
     — Dell
     — — Latitude
     — — — 5420
  ======================================================= */

  const getChildLabel =
    (
      category
    ) => {
      const rootLevel =
        Number(
          selectedRoot
            ?.level ||
            0
        );


      const categoryLevel =
        Number(
          category?.level ||
            0
        );


      const relativeLevel =
        Math.max(
          categoryLevel -
            rootLevel -
            1,

          0
        );


      const prefix =
        relativeLevel >
        0
          ? `${"— ".repeat(
              relativeLevel
            )}`
          : "";


      return `${prefix}${
        category?.name ||
          ""
      }`;
    };


  /* =======================================================
     ACTIVE FILTER CHECK
  ======================================================= */

  const hasActiveFilters =
    Boolean(
      filters?.search ||
      filters?.status ||
      filters?.category ||
      filters?.subCategory ||
      filters?.brand ||
      filters?.condition ||
      filters?.productType ||
      filters?.stockStatus ||
      filters?.minPrice ||
      filters?.maxPrice ||
      filters?.isFeatured
    );


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-product-filter-card"
    >
      {/* ===================================================
          PRIMARY FILTERS
      =================================================== */}

      <div
        className="inventory-product-filter-main-grid"
      >
        {/* =================================================
            SEARCH
        ================================================= */}

        <div
          className="inventory-product-filter-search-field"
        >
          <FiSearch />


          <input
            type="text"
            name="search"
            value={
              filters?.search ||
                ""
            }
            onChange={
              handleChange
            }
            onKeyDown={
              handleKeyDown
            }
            placeholder="Search name, SKU, barcode, brand or model"
            disabled={
              loading
            }
          />
        </div>


        {/* =================================================
            STATUS
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Status
          </label>


          <select
            name="status"
            value={
              filters?.status ||
                ""
            }
            onChange={
              handleChange
            }
            disabled={
              loading
            }
          >
            <option
              value=""
            >
              All Status
            </option>


            {PRODUCT_STATUSES.map(
              (
                status
              ) => (
                <option
                  key={
                    status.value
                  }
                  value={
                    status.value
                  }
                >
                  {status.label}
                </option>
              )
            )}
          </select>
        </div>


        {/* =================================================
            MAIN CATEGORY
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Main Category
          </label>


          <select
            name="category"
            value={
              selectedRootId
            }
            onChange={
              handleCategoryChange
            }
            disabled={
              loading ||
              categoryLoading
            }
          >
            <option
              value=""
            >
              {categoryLoading
                ? "Loading categories..."
                : "All Main Categories"}
            </option>


            {rootCategories.map(
              (
                category
              ) => (
                <option
                  key={
                    category._id
                  }
                  value={
                    category._id
                  }
                >
                  {category.name}
                </option>
              )
            )}
          </select>
        </div>


        {/* =================================================
            ANY DESCENDANT CATEGORY
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Child Category
          </label>


          <select
            name="subCategory"
            value={
              filters
                ?.subCategory ||
              ""
            }
            onChange={
              handleChange
            }
            disabled={
              loading ||
              categoryLoading ||
              !selectedRootId
            }
          >
            <option
              value=""
            >
              {!selectedRootId
                ? "Select main category first"
                : descendantCategories.length ===
                    0
                  ? "No child categories"
                  : "All Child Categories"}
            </option>


            {descendantCategories.map(
              (
                category
              ) => (
                <option
                  key={
                    category._id
                  }
                  value={
                    category._id
                  }
                  title={
                    category
                      ?._path ||
                    category
                      ?.path ||
                    category
                      ?.name
                  }
                >
                  {
                    getChildLabel(
                      category
                    )
                  }
                </option>
              )
            )}
          </select>
        </div>


        {/* =================================================
            BRAND
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Brand
          </label>


          <input
            type="text"
            name="brand"
            value={
              filters?.brand ||
                ""
            }
            onChange={
              handleChange
            }
            onKeyDown={
              handleKeyDown
            }
            placeholder="Example: Dell"
            disabled={
              loading
            }
          />
        </div>


        {/* =================================================
            CONDITION
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Condition
          </label>


          <select
            name="condition"
            value={
              filters
                ?.condition ||
              ""
            }
            onChange={
              handleChange
            }
            disabled={
              loading
            }
          >
            <option
              value=""
            >
              All Conditions
            </option>


            {PRODUCT_CONDITIONS.map(
              (
                condition
              ) => (
                <option
                  key={
                    condition.value
                  }
                  value={
                    condition.value
                  }
                >
                  {condition.label}
                </option>
              )
            )}
          </select>
        </div>


        {/* =================================================
            PRODUCT TYPE
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Product Type
          </label>


          <select
            name="productType"
            value={
              filters
                ?.productType ||
              ""
            }
            onChange={
              handleChange
            }
            disabled={
              loading
            }
          >
            <option
              value=""
            >
              All Types
            </option>


            {PRODUCT_TYPES.map(
              (
                type
              ) => (
                <option
                  key={
                    type.value
                  }
                  value={
                    type.value
                  }
                >
                  {type.label}
                </option>
              )
            )}
          </select>
        </div>


        {/* =================================================
            STOCK STATUS
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Stock Status
          </label>


          <select
            name="stockStatus"
            value={
              filters
                ?.stockStatus ||
              ""
            }
            onChange={
              handleChange
            }
            disabled={
              loading
            }
          >
            <option
              value=""
            >
              All Stock
            </option>


            {STOCK_STATUSES.map(
              (
                stockStatus
              ) => (
                <option
                  key={
                    stockStatus.value
                  }
                  value={
                    stockStatus.value
                  }
                >
                  {stockStatus.label}
                </option>
              )
            )}
          </select>
        </div>
      </div>


      {/* ===================================================
          SECONDARY FILTERS
      =================================================== */}

      <div
        className="inventory-product-filter-secondary-grid"
      >
        {/* =================================================
            MIN PRICE
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Minimum Price
          </label>


          <input
            type="text"
            inputMode="decimal"
            name="minPrice"
            value={
              filters?.minPrice ||
                ""
            }
            onChange={
              handleNumberChange
            }
            onKeyDown={
              handleKeyDown
            }
            placeholder="₹0"
            disabled={
              loading
            }
          />
        </div>


        {/* =================================================
            MAX PRICE
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Maximum Price
          </label>


          <input
            type="text"
            inputMode="decimal"
            name="maxPrice"
            value={
              filters?.maxPrice ||
                ""
            }
            onChange={
              handleNumberChange
            }
            onKeyDown={
              handleKeyDown
            }
            placeholder="₹100000"
            disabled={
              loading
            }
          />
        </div>


        {/* =================================================
            FEATURED
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Featured
          </label>


          <select
            name="isFeatured"
            value={
              filters
                ?.isFeatured ??
              ""
            }
            onChange={
              handleChange
            }
            disabled={
              loading
            }
          >
            <option
              value=""
            >
              All Products
            </option>


            <option
              value="true"
            >
              Featured
            </option>


            <option
              value="false"
            >
              Not Featured
            </option>
          </select>
        </div>


        {/* =================================================
            SORT BY
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Sort By
          </label>


          <select
            name="sortBy"
            value={
              filters
                ?.sortBy ||
              "createdAt"
            }
            onChange={
              handleChange
            }
            disabled={
              loading
            }
          >
            {SORT_OPTIONS.map(
              (
                option
              ) => (
                <option
                  key={
                    option.value
                  }
                  value={
                    option.value
                  }
                >
                  {option.label}
                </option>
              )
            )}
          </select>
        </div>


        {/* =================================================
            SORT ORDER
        ================================================= */}

        <div
          className="inventory-product-filter-field"
        >
          <label>
            Sort Order
          </label>


          <select
            name="sortOrder"
            value={
              filters
                ?.sortOrder ||
              "desc"
            }
            onChange={
              handleChange
            }
            disabled={
              loading
            }
          >
            <option
              value="desc"
            >
              Descending
            </option>


            <option
              value="asc"
            >
              Ascending
            </option>
          </select>
        </div>


        {/* =================================================
            ACTIONS
        ================================================= */}

        <div
          className="inventory-product-filter-actions"
        >
          <button
            type="button"
            className="inventory-product-filter-search"
            onClick={
              onSearch
            }
            disabled={
              loading
            }
          >
            <FiSearch />


            {loading
              ? "Searching..."
              : "Search"}
          </button>


          <button
            type="button"
            className="inventory-product-filter-reset"
            onClick={
              onReset
            }
            disabled={
              loading ||
              !hasActiveFilters
            }
          >
            <FiRefreshCcw />

            Reset
          </button>
        </div>
      </div>


      {/* ===================================================
          CATEGORY LOAD ERROR
      =================================================== */}

      {categoryError ? (
        <div
          className="inventory-product-category-load-error"
        >
          {categoryError}
        </div>
      ) : null}
    </div>
  );
};


export default ProductFilters;